import type { TFunction } from 'i18next';
import api from './api';

export interface NotificationType {
  code: string;
  label: string;
  group: string;
  default_priority: string;
}

/**
 * T1415: oznaka tipa obvestila v jeziku uporabnika.
 *
 * `label` iz registra je slovenski niz za vse tipe, zato ga odjemalec prevede
 * po kodi (`notifications.type_<code>`). Strežniška oznaka ostane rezerva —
 * nov tip brez ključa pokaže vsaj berljivo besedilo, ne surove kode;
 * varovalka `test_t1415_oznake_tipov_obvestil.py` zahteva ključ v vseh petih
 * jezikih.
 */
export function notificationTypeLabel(
  t: TFunction,
  nt: Pick<NotificationType, 'code' | 'label'>,
): string {
  return t(`notifications.type_${nt.code}`, { defaultValue: nt.label });
}

export interface NotificationPreferences {
  available_types: NotificationType[];
  email_digest: 'immediate' | 'daily' | 'weekly';
  email_enabled: boolean;
  in_app_enabled: boolean;
  type_preferences: Record<string, boolean>;
}

export interface Notification {
  id: string;
  type: string;
  priority: 'low' | 'medium' | 'high';
  title: string;
  body: string;
  context: Record<string, unknown>;
  action_url: string;
  project: { id: string; name: string } | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  expires_at: string | null;
  is_from_agent: boolean;
  agent_name: string;
}

export interface NotificationListResponse {
  count: number;
  unread_count: number;
  next: number | null;
  results: Notification[];
}

export interface NotificationListParams {
  is_read?: boolean;
  type?: string;
  page?: number;
}

export const fetchNotifications = (params?: NotificationListParams): Promise<NotificationListResponse> =>
  api.get('notifications/', { params }).then((r) => r.data);

export const markNotificationRead = (id: string): Promise<Notification> =>
  api.patch(`notifications/${id}/read/`).then((r) => r.data);

export const markAllNotificationsRead = (): Promise<{ updated: number }> =>
  api.post('notifications/mark-all-read/').then((r) => r.data);

export const fetchNotificationPreferences = (signal?: AbortSignal): Promise<NotificationPreferences> =>
  api.get('notifications/preferences/', { signal }).then((r) => r.data);

export interface NotificationPreferencesUpdate {
  email_enabled?: boolean;
  in_app_enabled?: boolean;
  email_digest?: 'immediate' | 'daily' | 'weekly';
  type_preferences?: Record<string, boolean>;
}

export const updateNotificationPreferences = (data: NotificationPreferencesUpdate): Promise<NotificationPreferences> =>
  api.put('notifications/preferences/', data).then((r) => r.data);
